import { createContext, Dispatch, SetStateAction } from "react";
import { FilterOptionsExtented, IIdNameStyle, IIdNameStyleIcon, SelectedOptions } from "../models/FilterModels/IFilterModel";
import { IFunctionPermissions } from "../common/api_interfaces/role/IFunctionPermissions";
import { ITag } from "../common/api_interfaces/tag/ITag";
import { PROJECT_SETTINGS_DEFAULT } from "../common/constants/ProjectConst";
import { ProjectSettings } from "../common/api_interfaces/project/ProjectSettings";
import { IAllStatusColumn } from "../common/api_interfaces/column/IAllStatusColumn";
import { IColumnDisplay } from "../common/api_interfaces/column/IColumnDisplay";
import { ITeamMemberDisplay } from "../common/api_interfaces/project/ITeamDisplay";
import { IRestrictBoardView } from "../common/api_interfaces/board/IRestrictBoardView";

export interface IBoardForProxy {
    id: number;
    name: string;
}

export interface ISelectedFastFilters {
    priority: IIdNameStyle | undefined;
    type: IIdNameStyleIcon | undefined;
    tag: ITag | undefined;
}

export interface BoardContextType {
    // Состояния данных
    boardIdNumber: number;
    columns: IColumnDisplay[];
    filteredColumns: IColumnDisplay[];
    boardName: string;
    projectId: number | undefined;
    isProxyBoard: boolean;
    abbreviationProject: string | undefined;
    allStatusColumn: IAllStatusColumn[];
    projectSettings: ProjectSettings;

    // Состояния UI
    actionMode: boolean;
    selectedTasks: number[];
    selectedTasksToColumn: number[];

    // -------
    showMenu: boolean;
    showTaskPopup: boolean;
    showConfirmDeleteTasks: boolean;
    showConfirmDeleteBoard: boolean;
    showConfirmDeleteColumn: boolean;
    addTaskColumnId: number | undefined;

    // Фильтры
    taskNameSearch: string;
    selectedFastFilters: ISelectedFastFilters;
    boardFilters: FilterOptionsExtented | undefined;
    selectedBoardFilters: SelectedOptions;
    filterCounter: number;

    // Данные
    relatedUsers: ITeamMemberDisplay[];
    boardsForProxy: IBoardForProxy[];
    colorOptions: string[];
    userAccess: IFunctionPermissions[];
    systemAccess: IFunctionPermissions[];
    accessRestrict: IRestrictBoardView | undefined;

    // Флаги доступа
    editStatusAcess: boolean;

    // Setter-функции
    setTaskNameSearch: Dispatch<SetStateAction<string>>;
    setBoardFilters: Dispatch<SetStateAction<FilterOptionsExtented | undefined>>;
    setSelectedBoardFilters: Dispatch<SetStateAction<SelectedOptions>>;
    setFilterCounter: Dispatch<SetStateAction<number>>;
    // setShowMenu: Dispatch<SetStateAction<boolean>>;
    setShowTaskPopup: Dispatch<SetStateAction<boolean>>;
    setAddTaskColumnId: Dispatch<SetStateAction<number | undefined>>;

    // Обработчики действий
    handleDeleteTask: (taskId: number) => void;
    handleDeleteTasks: (taskIds: number[]) => void;
    handleSelectTask: (taskId: number) => void;
    handleSelectTaskToColumn: (taskToColumnId: number) => void;
    handleSelectTasks: (taskIds: number[]) => void;
    handleSelectTasksToColumn: (taskToColumnIdss: number[]) => void;
    handleMoveTask: (taskId: number, newColumnId: number, position: number) => void;
    handleMoveTaskBoard: (taskId: number, boardId: number) => void;
    handleMoveTasks: (columnId: number) => void;
    handleMoveColumn: (columnId: number, position: number) => void;
    handleMoveTasksOutsideBoard: (columnId: number) => void;
    handleDeleteColumn: (columnId: number) => void;
    handleEditColumn: (columndId: number, newName?: string, newColor?: string, newWeight?: number, newStatus?: number) => void;
    handleRenameBoard: (newName: string) => void;
    handleDeleteBoard: () => void;
    handleAddColumn: (
        name: string,
        color: string,
        weight: number,
        columnStatus: number | undefined,
        columnPriority: number | undefined,
        proxyBoardName?: string,
        proxyBoardId?: number
    ) => void;
    handleMakeColumnCommon: (columnId: number) => void;
    handleMakeColumnProxy: (columnId: number, proxyBoardName?: string, proxyBoardId?: number) => void;
    handleTaskAction: () => void;

    // Вспомогательные функции
    changeFastFilters: (filters: ISelectedFastFilters) => void;
    cancelActionMode: () => void;
    openMenu: () => void;
    closeMenu: () => void;
    openAddTaskForm: (columnId: number) => void;
    resetFilter: () => void;
    deleteTasks: () => void;
    deleteColumn: () => void;
    deleteBoard: () => void;

    // Модальные окна
    // setTasksToDelete: Dispatch<SetStateAction<number[]>>;
    // setColumnToDelete: Dispatch<SetStateAction<IColumnDisplay | undefined>>;
    setShowConfirmDeleteTasks: Dispatch<SetStateAction<boolean>>;
    setShowConfirmDeleteBoard: Dispatch<SetStateAction<boolean>>;
    setShowConfirmDeleteColumn: Dispatch<SetStateAction<boolean>>;

    // Перезагрузка данных
    // reloadBoard: () => void;
}

export const BoardContext = createContext<BoardContextType>({
    boardIdNumber: -1,
    accessRestrict: undefined,
    handleDeleteTask: () => {/* non */},
    handleDeleteTasks: () => {/* non */},
    handleSelectTask: () => {/* non */},
    handleSelectTaskToColumn: () => {/* non */},
    handleSelectTasks: () => {/* non */},
    handleSelectTasksToColumn: () => {/* non */},
    handleMoveTask: () => {/* non */},
    handleMoveTaskBoard() {/* non */},
    handleMoveTasks: () => {/* non */},
    handleMoveColumn: () => {/* non */},
    handleMoveTasksOutsideBoard: () => {/* non */},
    columns: [],
    filteredColumns: [],
    taskNameSearch: "",
    setTaskNameSearch: () => {/* non */},
    setShowTaskPopup: () => {/* non */},
    setAddTaskColumnId: () => {/* non */},
    setShowConfirmDeleteTasks: () => {/* non */},
    setShowConfirmDeleteBoard: () => {/* non */},
    setShowConfirmDeleteColumn: () => {/* non */},
    selectedFastFilters: {
        priority: undefined,
        type: undefined,
        tag: undefined,
    },
    changeFastFilters: () => {/* non */},
    actionMode: false,
    cancelActionMode: () => {/* non */},
    selectedTasks: [],
    selectedTasksToColumn: [],
    relatedUsers: [],
    openMenu: () => {/* non */},
    closeMenu: () => {/* non */},
    boardName: "",
    handleDeleteColumn: () => {/* non */},
    handleEditColumn: () => {/* non */},
    openAddTaskForm: () => {/* non */},
    handleRenameBoard: () => {/* non */},
    handleDeleteBoard: () => {/* non */},
    handleAddColumn: () => {/* non */},
    boardsForProxy: [],
    handleMakeColumnCommon: () => {/* non */},
    handleMakeColumnProxy: () => {/* non */},
    colorOptions: [],
    boardFilters: {},
    setBoardFilters: () => {/* non */},
    selectedBoardFilters: {},
    setSelectedBoardFilters: () => {/* non */},
    filterCounter: 0,
    setFilterCounter: () => {/* non */},
    handleTaskAction: () => {/* non */},
    resetFilter: () => {/* non */},
    deleteTasks: () => {/* non */},
    deleteColumn: () => {/* non */},
    deleteBoard: () => {/* non */},
    projectId: -1,
    isProxyBoard: false,
    showTaskPopup: false,
    abbreviationProject: "-",
    allStatusColumn: [],
    addTaskColumnId: undefined,
    editStatusAcess: false,
    projectSettings: PROJECT_SETTINGS_DEFAULT,
    userAccess: [],
    showMenu: false,
    showConfirmDeleteTasks: false,
    showConfirmDeleteBoard: false,
    showConfirmDeleteColumn: false,
    systemAccess: []
});